import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { apiService } from '../../services/ApiService';

interface UseAssetSearchParams {
  selectedAssetId: string | undefined;
}

/** Debounced site search. Discard responses from an obsolete query/site or unmounted header. */
export const useAssetSearch = ({ selectedAssetId }: UseAssetSearchParams) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<{ locations: any[]; pois: any[] }>({ locations: [], pois: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);

  useEffect(() => {
    let current = true;
    const query = searchTerm.trim();
    const canSearch = query.length >= 2 && Boolean(selectedAssetId);
    setSearchResults({ locations: [], pois: [] });
    setSearchError(false);
    setSearchLoading(canSearch);
    setShowSearchResults(canSearch);
    if (!canSearch) return;

    const timeout = setTimeout(async () => {
      try {
        const result = await apiService.get(`/search?q=${encodeURIComponent(query)}&asset_id=${encodeURIComponent(selectedAssetId!)}`);
        if (current) setSearchResults({ locations: result.locations || [], pois: result.pois || [] });
      } catch (error) {
        if (current) { console.error('Failed to search:', error); setSearchError(true); }
      } finally {
        if (current) setSearchLoading(false);
      }
      // A completed request must not reopen a dropdown the user dismissed.
    }, 300);
    return () => { current = false; clearTimeout(timeout); };
  }, [searchTerm, selectedAssetId]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value);
  return { searchTerm, setSearchTerm, searchResults, showSearchResults, setShowSearchResults, searchLoading, searchError, handleSearchChange };
};
